import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Type,
  Heading1,
  Image,
  Square,
  Minus,
  GripVertical,
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  Pencil,
  Copy,
  Eye,
  Code,
  Columns,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Monitor,
  Tablet,
  Smartphone,
  Palette,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

// Block Types Configuration
const BLOCK_TYPES = {
  header: {
    name: 'Header',
    icon: Heading1,
    description: 'Add a branded header section',
    defaultContent: {
      title: 'Secret Sauce',
      subtitle: 'The PATH Programme',
      backgroundColor: '#24064C',
      textColor: '#ffffff',
    }
  },
  text: {
    name: 'Text Block',
    icon: Type,
    description: 'Rich text content',
    defaultContent: {
      text: '<p>Your content goes here. Use {{first_name}} for personalization.</p>',
      alignment: 'left',
      fontSize: '16',
      textColor: '#4b5563'
    }
  },
  heading: {
    name: 'Heading',
    icon: Heading1,
    description: 'Section heading',
    defaultContent: {
      text: 'Section Title',
      level: 'h2',
      alignment: 'left',
      textColor: '#1f2937'
    }
  },
  button: {
    name: 'Button',
    icon: Square,
    description: 'Call-to-action button',
    defaultContent: {
      text: 'Click Here',
      url: '{{dashboard_url}}',
      backgroundColor: '#24064C',
      textColor: '#ffffff',
      alignment: 'center',
      borderRadius: '8',
      fullWidth: false
    }
  },
  image: {
    name: 'Image',
    icon: Image,
    description: 'Add an image',
    defaultContent: {
      url: '',
      alt: '',
      width: '100',
      alignment: 'center',
      linkUrl: ''
    }
  },
  divider: {
    name: 'Divider',
    icon: Minus,
    description: 'Horizontal line separator',
    defaultContent: {
      color: '#e5e7eb',
      thickness: '1',
      margin: '24'
    }
  },
  spacer: {
    name: 'Spacer',
    icon: Columns,
    description: 'Empty vertical space',
    defaultContent: {
      height: '24'
    }
  },
  footer: {
    name: 'Footer',
    icon: Type,
    description: 'Email footer with legal info',
    defaultContent: {
      text: '© 2026 Secret Sauce. All rights reserved.',
      legalText: 'Secret Sauce is a trading name of Organisation Logic Limited, England & Wales Company Registration Number: 12213127',
      backgroundColor: '#f9fafb',
      textColor: '#9ca3af'
    }
  }
};

// Block Editor Panel Component
const BlockEditorPanel = ({ block, onChange, onClose }) => {
  const blockType = BLOCK_TYPES[block.type];

  const updateContent = (key, value) => {
    onChange({
      ...block,
      content: { ...block.content, [key]: value }
    });
  };

  return (
    <div className="p-4 space-y-4" data-testid="block-editor-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <blockType.icon className="w-4 h-4" />
          Edit {blockType.name}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>Done</Button>
      </div>

      {/* Header Block Editor */}
      {block.type === 'header' && (
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input
              value={block.content.title}
              onChange={(e) => updateContent('title', e.target.value)}
            />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input
              value={block.content.subtitle}
              onChange={(e) => updateContent('subtitle', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Background Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={block.content.backgroundColor}
                  onChange={(e) => updateContent('backgroundColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input
                  value={block.content.backgroundColor}
                  onChange={(e) => updateContent('backgroundColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label>Text Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={block.content.textColor}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input
                  value={block.content.textColor}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text Block Editor */}
      {block.type === 'text' && (
        <div className="space-y-3">
          <div>
            <Label>Content (HTML supported, use merge fields like {'{{first_name}}'})</Label>
            <Textarea
              value={block.content.text}
              onChange={(e) => updateContent('text', e.target.value)}
              rows={6}
              className="font-mono text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Alignment</Label>
              <Select value={block.content.alignment} onValueChange={(v) => updateContent('alignment', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Font Size</Label>
              <Input
                type="number"
                value={block.content.fontSize}
                onChange={(e) => updateContent('fontSize', e.target.value)}
              />
            </div>
            <div>
              <Label>Text Color</Label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={block.content.textColor}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Heading Block Editor */}
      {block.type === 'heading' && (
        <div className="space-y-3">
          <div>
            <Label>Heading Text</Label>
            <Input
              value={block.content.text}
              onChange={(e) => updateContent('text', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Level</Label>
              <Select value={block.content.level} onValueChange={(v) => updateContent('level', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="h1">H1 (Large)</SelectItem>
                  <SelectItem value="h2">H2 (Medium)</SelectItem>
                  <SelectItem value="h3">H3 (Small)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Alignment</Label>
              <Select value={block.content.alignment} onValueChange={(v) => updateContent('alignment', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={block.content.textColor}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Button Block Editor */}
      {block.type === 'button' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Button Text</Label>
              <Input
                value={block.content.text}
                onChange={(e) => updateContent('text', e.target.value)}
              />
            </div>
            <div>
              <Label>Link URL</Label>
              <Input
                value={block.content.url}
                onChange={(e) => updateContent('url', e.target.value)}
                placeholder="{{dashboard_url}}"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Background</Label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={block.content.backgroundColor}
                  onChange={(e) => updateContent('backgroundColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
            <div>
              <Label>Text Color</Label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={block.content.textColor}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
            <div>
              <Label>Alignment</Label>
              <Select value={block.content.alignment} onValueChange={(v) => updateContent('alignment', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Border Radius (px)</Label>
              <Input
                type="number"
                value={block.content.borderRadius}
                onChange={(e) => updateContent('borderRadius', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                checked={block.content.fullWidth}
                onChange={(e) => updateContent('fullWidth', e.target.checked)}
                id="fullWidth"
              />
              <Label htmlFor="fullWidth" className="cursor-pointer">Full Width</Label>
            </div>
          </div>
        </div>
      )}

      {/* Image Block Editor */}
      {block.type === 'image' && (
        <div className="space-y-3">
          <div>
            <Label>Image URL</Label>
            <Input
              value={block.content.url}
              onChange={(e) => updateContent('url', e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div>
            <Label>Alt Text</Label>
            <Input
              value={block.content.alt}
              onChange={(e) => updateContent('alt', e.target.value)}
              placeholder="Image description"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Width (%)</Label>
              <Input
                type="number"
                min="10"
                max="100"
                value={block.content.width}
                onChange={(e) => updateContent('width', e.target.value)}
              />
            </div>
            <div>
              <Label>Alignment</Label>
              <Select value={block.content.alignment} onValueChange={(v) => updateContent('alignment', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Link URL (optional)</Label>
            <Input
              value={block.content.linkUrl}
              onChange={(e) => updateContent('linkUrl', e.target.value)}
              placeholder="{{dashboard_url}}"
            />
          </div>
        </div>
      )}

      {/* Divider Block Editor */}
      {block.type === 'divider' && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Color</Label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={block.content.color}
                  onChange={(e) => updateContent('color', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
            <div>
              <Label>Thickness (px)</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={block.content.thickness}
                onChange={(e) => updateContent('thickness', e.target.value)}
              />
            </div>
            <div>
              <Label>Margin (px)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={block.content.margin}
                onChange={(e) => updateContent('margin', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Spacer Block Editor */}
      {block.type === 'spacer' && (
        <div>
          <Label>Height (px)</Label>
          <Input
            type="number"
            min="8"
            max="200"
            value={block.content.height}
            onChange={(e) => updateContent('height', e.target.value)}
          />
        </div>
      )}

      {/* Footer Block Editor */}
      {block.type === 'footer' && (
        <div className="space-y-3">
          <div>
            <Label>Copyright Text</Label>
            <Input
              value={block.content.text}
              onChange={(e) => updateContent('text', e.target.value)}
            />
          </div>
          <div>
            <Label>Legal Text</Label>
            <Textarea
              value={block.content.legalText}
              onChange={(e) => updateContent('legalText', e.target.value)}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Background Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={block.content.backgroundColor}
                  onChange={(e) => updateContent('backgroundColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
            <div>
              <Label>Text Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={block.content.textColor}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Block Preview Component
const BlockPreview = ({ block }) => {
  const renderBlock = () => {
    switch (block.type) {
      case 'header':
        return (
          <div
            className="p-8 rounded"
            style={{ backgroundColor: block.content.backgroundColor, color: block.content.textColor }}
          >
            <h2 className="text-xl font-bold m-0">{block.content.title}</h2>
            <p className="text-sm opacity-90 m-0 mt-1">{block.content.subtitle}</p>
          </div>
        );
      case 'text':
        return (
          <div
            style={{
              textAlign: block.content.alignment,
              fontSize: `${block.content.fontSize}px`,
              color: block.content.textColor
            }}
            dangerouslySetInnerHTML={{ __html: block.content.text }}
          />
        );
      case 'heading':
        const HeadingTag = block.content.level;
        const headingSizes = { h1: 'text-2xl', h2: 'text-xl', h3: 'text-lg' };
        return (
          <HeadingTag
            className={`${headingSizes[block.content.level]} font-semibold m-0`}
            style={{ textAlign: block.content.alignment, color: block.content.textColor }}
          >
            {block.content.text}
          </HeadingTag>
        );
      case 'button':
        return (
          <div style={{ textAlign: block.content.alignment }}>
            <button
              className="font-medium px-6 py-3 inline-block"
              style={{
                backgroundColor: block.content.backgroundColor,
                color: block.content.textColor,
                borderRadius: `${block.content.borderRadius}px`,
                width: block.content.fullWidth ? '100%' : 'auto'
              }}
            >
              {block.content.text}
            </button>
          </div>
        );
      case 'image':
        if (!block.content.url) {
          return (
            <div className="flex items-center justify-center p-8 bg-muted/30 border-2 border-dashed rounded">
              <Image className="w-8 h-8 text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">No image URL set</span>
            </div>
          );
        }
        return (
          <div style={{ textAlign: block.content.alignment }}>
            <img
              src={block.content.url}
              alt={block.content.alt}
              style={{ width: `${block.content.width}%`, maxWidth: '100%' }}
              className="inline-block"
            />
          </div>
        );
      case 'divider':
        return (
          <hr
            style={{
              borderColor: block.content.color,
              borderWidth: `${block.content.thickness}px`,
              margin: `${block.content.margin}px 0`
            }}
          />
        );
      case 'spacer':
        return <div style={{ height: `${block.content.height}px` }} />;
      case 'footer':
        return (
          <div
            className="p-6 text-center rounded"
            style={{ backgroundColor: block.content.backgroundColor, color: block.content.textColor }}
          >
            <p className="text-sm m-0">{block.content.text}</p>
            <p className="text-xs m-0 mt-2 opacity-75">{block.content.legalText}</p>
          </div>
        );
      default:
        return <div className="p-4 bg-muted/30 text-center">Unknown block type</div>;
    }
  };

  return <div className="block-preview">{renderBlock()}</div>;
};

// Main EmailBlockBuilder Component
export function EmailBlockBuilder({ blocks = [], onChange, onExportHtml }) {
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [activeTab, setActiveTab] = useState('builder');

  const deviceWidths = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px'
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      content: { ...BLOCK_TYPES[type].defaultContent }
    };
    onChange([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
    toast.success(`${BLOCK_TYPES[type].name} added`);
  };

  const updateBlock = (updatedBlock) => {
    onChange(blocks.map(b => b.id === updatedBlock.id ? updatedBlock : b));
  };

  const deleteBlock = (blockId) => {
    onChange(blocks.filter(b => b.id !== blockId));
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
    toast.success('Block removed');
  };

  const duplicateBlock = (blockId) => {
    const blockToDuplicate = blocks.find(b => b.id === blockId);
    if (blockToDuplicate) {
      const newBlock = {
        ...blockToDuplicate,
        id: `block_${Date.now()}`,
        content: { ...blockToDuplicate.content }
      };
      const index = blocks.findIndex(b => b.id === blockId);
      const newBlocks = [...blocks];
      newBlocks.splice(index + 1, 0, newBlock);
      onChange(newBlocks);
      toast.success('Block duplicated');
    }
  };

  const moveBlock = (blockId, direction) => {
    const index = blocks.findIndex(b => b.id === blockId);
    if (direction === 'up' && index > 0) {
      const newBlocks = [...blocks];
      [newBlocks[index], newBlocks[index - 1]] = [newBlocks[index - 1], newBlocks[index]];
      onChange(newBlocks);
    } else if (direction === 'down' && index < blocks.length - 1) {
      const newBlocks = [...blocks];
      [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
      onChange(newBlocks);
    }
  };

  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  // Generate HTML from blocks
  const generateHtml = useCallback(() => {
    let contentHtml = '';

    blocks.forEach(block => {
      switch (block.type) {
        case 'header':
          contentHtml += `
            <tr>
              <td style="background: linear-gradient(135deg, ${block.content.backgroundColor} 0%, #7C3AED 100%); padding: 40px 40px 30px;">
                <h1 style="margin: 0; font-size: 28px; font-weight: 700; color: ${block.content.textColor}; font-family: 'Poppins', sans-serif;">${block.content.title}</h1>
                <p style="margin: 8px 0 0; font-size: 14px; color: ${block.content.textColor}; opacity: 0.9;">${block.content.subtitle}</p>
              </td>
            </tr>`;
          break;
        case 'text':
          contentHtml += `
            <tr>
              <td style="padding: 20px 40px; text-align: ${block.content.alignment}; font-size: ${block.content.fontSize}px; color: ${block.content.textColor}; line-height: 1.6;">
                ${block.content.text}
              </td>
            </tr>`;
          break;
        case 'heading':
          const sizes = { h1: '28px', h2: '24px', h3: '20px' };
          contentHtml += `
            <tr>
              <td style="padding: 20px 40px;">
                <${block.content.level} style="margin: 0; font-size: ${sizes[block.content.level]}; font-weight: 600; color: ${block.content.textColor}; text-align: ${block.content.alignment}; font-family: 'Poppins', Georgia, serif;">
                  ${block.content.text}
                </${block.content.level}>
              </td>
            </tr>`;
          break;
        case 'button':
          const alignStyle = block.content.alignment === 'center' ? 'margin: 0 auto;' : 
                            block.content.alignment === 'right' ? 'margin-left: auto;' : '';
          contentHtml += `
            <tr>
              <td style="padding: 20px 40px; text-align: ${block.content.alignment};">
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="${alignStyle}">
                  <tr>
                    <td>
                      <a href="${block.content.url}" style="display: inline-block; padding: 14px 28px; font-size: 16px; font-weight: 600; color: ${block.content.textColor}; background-color: ${block.content.backgroundColor}; border-radius: ${block.content.borderRadius}px; text-decoration: none; ${block.content.fullWidth ? 'width: 100%; text-align: center;' : ''}">
                        ${block.content.text}
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>`;
          break;
        case 'image':
          if (block.content.url) {
            const imgHtml = block.content.linkUrl 
              ? `<a href="${block.content.linkUrl}"><img src="${block.content.url}" alt="${block.content.alt}" style="width: ${block.content.width}%; max-width: 100%; display: block;" /></a>`
              : `<img src="${block.content.url}" alt="${block.content.alt}" style="width: ${block.content.width}%; max-width: 100%; display: block;" />`;
            contentHtml += `
              <tr>
                <td style="padding: 20px 40px; text-align: ${block.content.alignment};">
                  ${imgHtml}
                </td>
              </tr>`;
          }
          break;
        case 'divider':
          contentHtml += `
            <tr>
              <td style="padding: ${block.content.margin}px 40px;">
                <hr style="border: 0; border-top: ${block.content.thickness}px solid ${block.content.color}; margin: 0;" />
              </td>
            </tr>`;
          break;
        case 'spacer':
          contentHtml += `
            <tr>
              <td style="height: ${block.content.height}px;"></td>
            </tr>`;
          break;
        case 'footer':
          contentHtml += `
            <tr>
              <td style="background-color: ${block.content.backgroundColor}; padding: 24px 40px; border-top: 1px solid #e5e7eb;">
                <p style="margin: 0 0 8px; font-size: 12px; color: ${block.content.textColor}; text-align: center;">
                  ${block.content.text}
                </p>
                <p style="margin: 0; font-size: 11px; color: ${block.content.textColor}; text-align: center; opacity: 0.75;">
                  ${block.content.legalText}
                </p>
              </td>
            </tr>`;
          break;
      }
    });

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: 'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f5f5f5;">
        <tr>
            <td style="padding: 40px 20px;">
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    ${contentHtml}
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
  }, [blocks]);

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="email-block-builder">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/20">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-8">
            <TabsTrigger value="builder" className="text-xs h-7 px-3">
              <Settings className="w-3 h-3 mr-1" />
              Builder
            </TabsTrigger>
            <TabsTrigger value="preview" className="text-xs h-7 px-3">
              <Eye className="w-3 h-3 mr-1" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="code" className="text-xs h-7 px-3">
              <Code className="w-3 h-3 mr-1" />
              HTML
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab === 'preview' && (
          <div className="flex items-center gap-1">
            {[
              { key: 'desktop', icon: Monitor },
              { key: 'tablet', icon: Tablet },
              { key: 'mobile', icon: Smartphone }
            ].map(device => (
              <Button
                key={device.key}
                variant={previewDevice === device.key ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPreviewDevice(device.key)}
                className="h-7 w-7 p-0"
              >
                <device.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        )}

        {onExportHtml && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportHtml(generateHtml())}
            className="h-7"
          >
            Export HTML
          </Button>
        )}
      </div>

      {/* Builder View */}
      {activeTab === 'builder' && (
        <div className="flex">
          {/* Block Palette */}
          <div className="w-48 border-r border-border p-3 bg-muted/10">
            <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Add Blocks</p>
            <div className="space-y-1">
              {Object.entries(BLOCK_TYPES).map(([type, config]) => (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  className="w-full flex items-center gap-2 p-2 text-sm rounded hover:bg-muted transition-colors text-left"
                  data-testid={`add-block-${type}`}
                >
                  <config.icon className="w-4 h-4 text-muted-foreground" />
                  <span>{config.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 p-4 min-h-[500px] bg-muted/5">
            {blocks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                <Plus className="w-12 h-12 mb-4 opacity-30" />
                <p className="text-lg font-medium mb-1">Start Building</p>
                <p className="text-sm">Click a block type on the left to add it</p>
              </div>
            ) : (
              <div className="space-y-2">
                {blocks.map((block, index) => (
                  <div
                    key={block.id}
                    className={`group relative border rounded-lg transition-all ${
                      selectedBlockId === block.id
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-border hover:border-muted-foreground/50'
                    }`}
                    onClick={() => setSelectedBlockId(block.id)}
                  >
                    {/* Block Controls */}
                    <div className="absolute -left-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 'up'); }}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 'down'); }}
                        disabled={index === blocks.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="absolute -right-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => { e.stopPropagation(); duplicateBlock(block.id); }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Block Type Badge */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Badge variant="secondary" className="text-xs">
                        {BLOCK_TYPES[block.type].name}
                      </Badge>
                    </div>

                    {/* Block Content Preview */}
                    <div className="p-4">
                      <BlockPreview block={block} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Editor Panel */}
          {selectedBlock && (
            <div className="w-80 border-l border-border bg-card">
              <BlockEditorPanel
                block={selectedBlock}
                onChange={updateBlock}
                onClose={() => setSelectedBlockId(null)}
              />
            </div>
          )}
        </div>
      )}

      {/* Preview View */}
      {activeTab === 'preview' && (
        <div className="flex justify-center p-6 bg-muted/30 min-h-[600px]">
          <div
            className="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-300"
            style={{ width: deviceWidths[previewDevice], maxWidth: '100%' }}
          >
            <iframe
              srcDoc={generateHtml()}
              title="Email Preview"
              className="w-full min-h-[550px] border-0"
            />
          </div>
        </div>
      )}

      {/* Code View */}
      {activeTab === 'code' && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Generated HTML</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(generateHtml());
                toast.success('HTML copied to clipboard');
              }}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy
            </Button>
          </div>
          <pre className="p-4 bg-muted/30 rounded-lg overflow-auto max-h-[500px] text-xs font-mono">
            {generateHtml()}
          </pre>
        </div>
      )}
    </div>
  );
}

export default EmailBlockBuilder;
